import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faCloudSun, faCloudSunRain, faCloudShowersHeavy } from '@fortawesome/free-solid-svg-icons';
import { apiFetch } from '../../api/apiClient';
import { selectNextHourlyForecast } from '../../util/weatherForecastUtils';
import { formatTemperature } from '../../util/temperature';
import { useTemperatureUnit } from '../../util/useTemperatureUnit';

export async function loadWeatherForecastData(portfolioName) {
  if (!portfolioName) {
    return { dailyForecast: [], hourlyForecast: [] };
  }

  const [dailyResponse, hourlyResponse] = await Promise.all([
    apiFetch(`/api/weather/portfolio/${portfolioName}/forecast/daily`),
    apiFetch(`/api/weather/portfolio/${portfolioName}/forecast/hourly`),
  ]);

  const dailyItems = Array.isArray(dailyResponse.forecast) ? dailyResponse.forecast : [];
  const hourlyItems = Array.isArray(hourlyResponse.forecast) ? hourlyResponse.forecast : [];

  return {
    dailyForecast: dailyItems.slice(0, 7),
    hourlyForecast: hourlyItems,
  };
}

const hasForecastData = (data) =>
  data != null
  && Array.isArray(data.dailyForecast)
  && Array.isArray(data.hourlyForecast);

export default function WeatherForecast({ portfolioName = '', data }) {
  const temperatureUnit = useTemperatureUnit();
  const [view, setView] = useState("daily"); // "daily" | "hourly"
  const [localDailyForecast, setLocalDailyForecast] = useState([]);
  const [localHourlyForecast, setLocalHourlyForecast] = useState([]);

  const useProvidedData = hasForecastData(data);
  const dailyForecast = useProvidedData ? data.dailyForecast : localDailyForecast;
  const hourlyForecast = useProvidedData ? data.hourlyForecast : localHourlyForecast;
  const nextHourlyForecast = useMemo(
    () => selectNextHourlyForecast(hourlyForecast),
    [hourlyForecast],
  );

  const TEMP_HIGH_COLOR = 'rgb(220, 38, 38)';
  const TEMP_LOW_COLOR = 'rgb(37, 99, 235)';

  const getTempValue = (entry) => {
    const value = entry?.temperatureF;
    return value === undefined ? "--" : formatTemperature(value, temperatureUnit, { fallback: '--' });
  };

  const formatTemp = (value) => formatTemperature(value, temperatureUnit, { fallback: '--' });

  // Gradient runs directly from blue (<=60°F) to red (>=110°F).
  const TEMP_COLOR_STOPS = [
    { temp: 60, color: [37, 99, 235] },   // blue
    { temp: 110, color: [220, 38, 38] },  // red
  ];

  const interpolateChannel = (a, b, t) => Math.round(a + (b - a) * t);

  const getTempColor = (entry) => {
    const value = Number(entry?.temperatureF);
    if (!Number.isFinite(value)) {
      return "inherit";
    }
    const first = TEMP_COLOR_STOPS[0];
    const last = TEMP_COLOR_STOPS[TEMP_COLOR_STOPS.length - 1];
    if (value <= first.temp) {
      return `rgb(${first.color.join(", ")})`;
    }
    if (value >= last.temp) {
      return `rgb(${last.color.join(", ")})`;
    }
    for (let i = 0; i < TEMP_COLOR_STOPS.length - 1; i += 1) {
      const lower = TEMP_COLOR_STOPS[i];
      const upper = TEMP_COLOR_STOPS[i + 1];
      if (value >= lower.temp && value <= upper.temp) {
        const t = (value - lower.temp) / (upper.temp - lower.temp);
        const r = interpolateChannel(lower.color[0], upper.color[0], t);
        const g = interpolateChannel(lower.color[1], upper.color[1], t);
        const b = interpolateChannel(lower.color[2], upper.color[2], t);
        return `rgb(${r}, ${g}, ${b})`;
      }
    }
    return "inherit";
  };

  const renderTempRange = (entry) => {
    const high = formatTemp(entry?.temperatureMaxF ?? entry?.temperatureF);
    const low = formatTemp(entry?.temperatureMinF ?? entry?.temperatureF);

    return (
      <>
        <span style={{ color: TEMP_HIGH_COLOR }}>{high}</span>
        {' / '}
        <span style={{ color: TEMP_LOW_COLOR }}>{low}</span>
      </>
    );
  };

  // For forecast data, field will be entry.day, format: "Thu 04/30"
  const formatDailyLabel = (entry) => {
    const rawDay = entry?.day;
    if (!rawDay) {
      return "--";
    }
    // We expect rawDay already in "Thu 04/30" format
    return rawDay;
  };

  const formatHourlyDate = (entry) => {
    const dateKey = entry?.date;
    if (dateKey) {
      const [year, month, day] = dateKey.split('-').map(Number);
      if (year && month && day) {
        return `${month}/${day}`;
      }
    }
    const now = new Date();
    return `${now.getMonth() + 1}/${now.getDate()}`;
  };

  const formatHourlyLabel = (entry) => {
    const rawTime = entry?.time;
    if (!rawTime) {
      return "--";
    }
    const hour = parseInt(rawTime.split(":")[0], 10);
    const period = hour < 12 ? "AM" : "PM";
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour} ${period} : ${formatHourlyDate(entry)}`;
  };

  const iconForEntry = (entry) => {
    const descriptor = `${entry?.conditions || ""}`.toLowerCase();
    switch (descriptor) {
      case "sunny":
      case "clear sky":
      case "mainly clear":
        return faSun;
      case "partly sunny":
      case "partly cloudy":
        return faCloudSunRain;
      case "cloudy":
      case "overcast":
        return faCloudSun;
      case "windy":
        return faCloudSun;
      case "rain":
      case "snow":
      case "thunderstorm":
        return faCloudShowersHeavy;
      default:
        return faSun;
    }
  };

  useEffect(() => {
    if (useProvidedData) {
      return undefined;
    }

    let isActive = true;

    const loadForecast = async () => {
      if (!portfolioName) {
        return;
      }

      try {
        const { dailyForecast: dailyItems, hourlyForecast: hourlyItems } =
          await loadWeatherForecastData(portfolioName);

        if (!isActive) {
          return;
        }

        setLocalDailyForecast(dailyItems);
        setLocalHourlyForecast(hourlyItems);
      } catch (error) {
        console.error('Unable to load weather forecast:', error);
      }
    };

    loadForecast();

    return () => {
      isActive = false;
    };
  }, [portfolioName, useProvidedData]);

  return (
    <div id="forecast" className="panel component p-3 mb-5">
      <strong className="text-center d-block mb-3 fs-5">WEATHER FORECAST</strong>
      {view === "daily" && (
        <div className="daily">
          <div className="header text-center d-flex justify-content-evenly mb-4">
            <span>DAILY</span> |
            <button onClick={() => setView("hourly")}>
              HOURLY
            </button>
          </div>
          {dailyForecast.map((entry, index) => (
            <div key={`daily-${index}`}>
              <FontAwesomeIcon icon={iconForEntry(entry)} />
              <strong>{renderTempRange(entry)}</strong>
              <span>{formatDailyLabel(entry)}</span>
            </div>
          ))}
        </div>
      )}

      {view === "hourly" && (
        <div className="hourly">
          <div className="header text-center d-flex justify-content-evenly mb-4">
            <button onClick={() => setView("daily")}>
              DAILY
            </button>
            | <span>HOURLY</span>
          </div>
          {nextHourlyForecast.map((entry, index) => (
            <div key={`hourly-${index}`}>
              <FontAwesomeIcon icon={iconForEntry(entry)} />
              <strong style={{ color: getTempColor(entry) }}>{getTempValue(entry)}</strong>
              <span>{formatHourlyLabel(entry)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}